import { useEffect, useMemo } from "react"
import { useQuery } from "@tanstack/react-query"
import {
  myBrandDrawerDataQueryOptions,
  type DrilldownPageFiltersInput,
  type DrilldownQuerySection,
} from "@/lib/queries/drilldown.queries"
import type { MyBrandFiltersInput } from "@/lib/queries/dashboard.queries"
import type { MyBrandCreativeCardModel } from "../lib/drilldown-card.types"
import {
  myBrandDrawerCacheKey,
  peekCachedMyBrandDrawerData,
  setCachedMyBrandDrawerData,
  touchCachedMyBrandDrawerData,
} from "./useMyBrandDrawerDataCache"

export function useMyBrandDrawerDataSection(
  creative: MyBrandCreativeCardModel | null,
  open: boolean,
  myBrandFilters: MyBrandFiltersInput,
  drilldownFilters: DrilldownPageFiltersInput,
): {
  section: DrilldownQuerySection | null
  isLoading: boolean
  isError: boolean
} {
  const hash = creative?.hash ?? ""
  const adId = creative?.adId ?? null
  const cacheKey =
    hash ? myBrandDrawerCacheKey(hash, adId, myBrandFilters, drilldownFilters) : ""
  const cached = useMemo(
    () => (cacheKey ? peekCachedMyBrandDrawerData(cacheKey) : undefined),
    [cacheKey],
  )

  useEffect(() => {
    if (cacheKey && cached) touchCachedMyBrandDrawerData(cacheKey)
  }, [cacheKey, cached])

  const shouldFetch = open && !!cacheKey && !cached

  const { data, isLoading, isError } = useQuery(
    myBrandDrawerDataQueryOptions(hash, adId, myBrandFilters, drilldownFilters, shouldFetch),
  )

  useEffect(() => {
    if (data && cacheKey) setCachedMyBrandDrawerData(cacheKey, data)
  }, [data, cacheKey])

  const section = cached ?? data ?? null

  return { section, isLoading: shouldFetch && isLoading, isError }
}
